import * as React from 'react';
import styled, { css } from 'styled-components';

import type { ControlWidth } from '@packages/core';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { mediaQuery } from '../../tokens/breakpoints';
import { rem } from '../../tokens/spacing';
import { isBoundedControlWidth } from './controlWidth';
import { FormFieldShell } from './FormFieldShell';

export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Field label. When omitted in `control-first` mode, the child control is
   * expected to render its own label (e.g. Checkbox with wrapping <label>). */
  label?: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  orientation?: 'vertical' | 'horizontal';
  /** Caps control width inside the grid cell. Defaults to `auto` (fill cell). */
  controlWidth?: ControlWidth;
  /**
   * `label-first` — label above (vertical) or left (horizontal). Matches legacy `FormWrapper` + `Label`.
   * `control-first` — control then label (`gap: 17px`), matching legacy `RadioButtonWrapper` + `LabelNormal`.
   */
  pairOrder?: 'label-first' | 'control-first';
  /**
   * Extra spacing between the field label and the control below it (vertical orientation only).
   * Defaults to `'sm'` (4px — suits single-line inputs).
   * Use `'md'` (8px) for radio groups and checkboxes where the options are inline/row-level.
   */
  labelSpacing?: 'sm' | 'md';
  /**
   * When `true`, reserve vertical space equivalent to a standard label row
   * (26px) even when no visible label is present. This keeps the control area
   * vertically aligned with labeled siblings in a CSS grid row.
   *
   * Defaults to `false`. The form engine sets this automatically for
   * checkbox / toggle fields placed alongside labeled inputs.
   */
  reserveLabelSpace?: boolean;
  semanticId?: string;
  children: React.ReactNode;
}

const shouldForwardTransient = (prop: string | number): boolean =>
  !['$orientation', '$pairOrder', '$labelSpacing'].includes(String(prop));

const Root = styled.div.withConfig({ shouldForwardProp: shouldForwardTransient })<{
  $orientation: 'vertical' | 'horizontal';
  $pairOrder: 'label-first' | 'control-first';
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0; /* allow shrink inside FormGrid so compound fields (phone) don't overflow */
  gap: 0;
`;

/** Legacy `RadioButtonWrapper`: checkbox/toggle then label, gap 17px. */
const ControlFirstRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(17)};
  min-width: 0;
`;

const LabelRow = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$orientation', '$reserveHeight'].includes(String(prop)),
})<{ $orientation: 'vertical' | 'horizontal'; $reserveHeight: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(4)};
  flex-shrink: 0;
  width: ${({ $orientation }) => ($orientation === 'vertical' ? '100%' : 'auto')};

  ${({ $reserveHeight }) =>
    $reserveHeight &&
    css`
      min-height: ${rem(26)};
    `}

  ${({ $orientation }) =>
    $orientation === 'horizontal' &&
    css`
      flex: 0 1 auto;
      min-width: 0;
      max-width: 62%;
    `}
`;

/**
 * Legacy `Label` / `LabelNormal` (`input.styled.jsx`): 16px, weight 400, line-height 22, brand sans.
 */
const FieldLabel = styled.label<{ $marginBelow: false | 'sm' | 'md'; $allowWrap: boolean }>`
  display: block;
  margin: 0;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: ${rem(22)};
  color: var(--ui-text);
  white-space: ${({ $allowWrap }) => ($allowWrap ? 'normal' : 'nowrap')};
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ $marginBelow }) =>
    $marginBelow === 'sm' &&
    css`
      margin-bottom: ${rem(4)};
    `}
  ${({ $marginBelow }) =>
    $marginBelow === 'md' &&
    css`
      margin-bottom: ${rem(8)};
    `}
`;

const RequiredMark = styled.span`
  display: inline;
  margin-left: ${rem(4)};
  color: var(--ui-status-danger);
  font-weight: 600;
  line-height: 1;
`;

const HorizontalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${rem(12)};
  width: 100%;

  ${mediaQuery.sm} {
    flex-direction: row;
    align-items: center;
  }
`;

const ControlColumn = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$orientation' && prop !== '$boundedWidth',
})<{ $orientation: 'vertical' | 'horizontal'; $boundedWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
  min-width: 0;
  width: 100%;

  ${({ $orientation, $boundedWidth }) =>
    $orientation === 'horizontal'
      ? css`
          flex: 1 1 0;
          align-items: ${$boundedWidth ? 'flex-start' : 'stretch'};
        `
      : css`
          flex: 0 0 auto;
          align-items: ${$boundedWidth ? 'flex-start' : 'stretch'};
        `}
`;

const ControlSlot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${rem(12)};
  line-height: ${rem(14)};
  color: var(--ui-text-muted);
`;

const ErrorText = styled.p`
  margin: 0;
  font-weight: 400;
  font-size: ${rem(12)};
  line-height: ${rem(14)};
  color: var(--ui-status-danger);
  margin-top: ${rem(6)};
  transition: all 0.2s linear;
`;

export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(function FormField(
  {
    label,
    htmlFor,
    error,
    hint,
    required = false,
    disabled = false,
    orientation = 'vertical',
    controlWidth = 'auto',
    pairOrder = 'label-first',
    labelSpacing = 'sm',
    reserveLabelSpace = false,
    semanticId,
    children,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const hasError = Boolean(error && error.length > 0);
  const dataState: 'error' | 'valid' | 'default' = hasError
    ? 'error'
    : disabled
      ? 'default'
      : 'valid';

  const boundedWidth = isBoundedControlWidth(controlWidth);
  const control = <FormFieldShell $controlWidth={controlWidth}>{children}</FormFieldShell>;

  const messages = (
    <>
      {hint != null && hint !== '' ? <Hint>{hint}</Hint> : null}
      {hasError ? <ErrorText role="alert">{error}</ErrorText> : null}
    </>
  );

  const labelId = htmlFor ? `${htmlFor}-label` : undefined;

  return (
    <Root
      ref={ref}
      $orientation={orientation}
      $pairOrder={pairOrder}
      data-state={dataState}
      data-disabled={disabled || undefined}
      {...semanticProps}
      {...rest}
    >
      {pairOrder === 'control-first' ? (
        <>
          {reserveLabelSpace && (
            <LabelRow $orientation="vertical" $reserveHeight aria-hidden="true" />
          )}
          <ControlFirstRow>
            <ControlSlot>{control}</ControlSlot>
            {label != null && label !== '' && (
              <FieldLabel htmlFor={htmlFor} id={labelId} $marginBelow={false} $allowWrap>
                {label}
                {required ? <RequiredMark aria-hidden="true">*</RequiredMark> : null}
              </FieldLabel>
            )}
          </ControlFirstRow>
          {messages}
        </>
      ) : orientation === 'horizontal' ? (
        <HorizontalBody>
          <LabelRow $orientation="horizontal" $reserveHeight={false}>
            <FieldLabel htmlFor={htmlFor} id={labelId} $marginBelow={false} $allowWrap={false}>
              {label}
              {required ? <RequiredMark aria-hidden="true">*</RequiredMark> : null}
            </FieldLabel>
          </LabelRow>
          <ControlColumn $orientation="horizontal" $boundedWidth={boundedWidth}>
            {control}
            {messages}
          </ControlColumn>
        </HorizontalBody>
      ) : (
        <>
          <LabelRow $orientation="vertical" $reserveHeight={reserveLabelSpace === true || (reserveLabelSpace !== false && (label == null || label === ''))}>
            <FieldLabel htmlFor={htmlFor} id={labelId} $marginBelow={labelSpacing} $allowWrap={false}>
              {label}
              {required ? <RequiredMark aria-hidden="true">*</RequiredMark> : null}
            </FieldLabel>
          </LabelRow>
          <ControlColumn $orientation="vertical" $boundedWidth={boundedWidth}>
            {control}
            {messages}
          </ControlColumn>
        </>
      )}
    </Root>
  );
});

FormField.displayName = 'FormField';
